import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion } from "framer-motion";
import { LayoutGrid, List, ExternalLink } from "lucide-react";
import { categoryProducts, type Product } from "@/data/products";
import { Badge } from "@/components/ui/badge";
import SEOHead from "@/components/SEOHead";

type ViewMode = "grid" | "list";
type FilterType = "all" | "electric" | "servo-hydraulic" | "robot" | "auxiliary";

function ProductCardGrid({ product, lang, t, showBadges }: { product: Product; lang: string; t: (k: string) => string; showBadges: boolean }) {
  return (
    <a
      href={product.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group block bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {showBadges && (
          <Badge className="absolute top-3 right-3 text-xs" variant={product.type === "electric" ? "default" : "secondary"}>
            {product.type === "electric" ? t("equipment.electric") : product.type === "robot" ? t("equipment.servoDrive") : t("equipment.servoHydraulic")}
          </Badge>
        )}
      </div>
      <div className="p-5">
        <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
          {product.name}
        </h3>
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {product.description[lang as "ro" | "en"]}
        </p>
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
          {t("equipment.viewDetails")}
          <ExternalLink className="w-3.5 h-3.5" />
        </span>
      </div>
    </a>
  );
}

function ProductCardList({ product, lang, t, showBadges }: { product: Product; lang: string; t: (k: string) => string; showBadges: boolean }) {
  return (
    <a
      href={product.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-row bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all"
    >
      <div className="relative w-[30%] sm:w-64 shrink-0 aspect-square sm:aspect-auto sm:h-48 overflow-hidden bg-muted rounded-l-xl">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="flex-1 p-5 flex flex-col justify-center">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors">
            {product.name}
          </h3>
          {showBadges && (
            <Badge className="shrink-0 text-xs" variant={product.type === "electric" ? "default" : "secondary"}>
              {product.type === "electric" ? t("equipment.electric") : product.type === "robot" ? t("equipment.servoDrive") : t("equipment.servoHydraulic")}
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          {product.description[lang as "ro" | "en"]}
        </p>
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
          {t("equipment.viewDetails")}
          <ExternalLink className="w-3.5 h-3.5" />
        </span>
      </div>
    </a>
  );
}

export default function EquipmentDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { lang, t } = useLanguage();
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [filter, setFilter] = useState<FilterType>("all");

  const category = slug ? categoryProducts[slug] : undefined;

  if (!category) {
    return (
      <div className="py-16 section-container text-center">
        <p className="text-muted-foreground">Page not found</p>
        <Link to="/echipamente" className="text-primary font-medium mt-4 inline-block">
          {t("equipment.backToEquipment")}
        </Link>
      </div>
    );
  }

  const hasTypeFilter = category.products.some((p) => p.type === "electric") && category.products.some((p) => p.type === "servo-hydraulic");

  const filteredProducts = filter === "all" ? category.products : category.products.filter((p) => p.type === filter);

  return (
    <div className="py-16 lg:py-24">
      <SEOHead
        title={t(category.titleKey)}
        description={t(category.descKey)}
        path={`/echipamente/${slug}`}
      />
      <div className="section-container">
        <Link to="/echipamente" className="text-sm text-primary font-medium hover:underline mb-6 inline-block">
          {t("equipment.backToEquipment")}
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-2">{t(category.titleKey)}</h1>
          <p className="text-muted-foreground">{t(category.descKey)}</p>
        </motion.div>

        {category.products.length === 0 ? (
          <p className="text-muted-foreground text-center py-20">{t("equipment.noProducts")}</p>
        ) : (
          <>
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8 pb-4 border-b border-border">
              {/* Filter */}
              {hasTypeFilter && (
                <div className="flex items-center gap-2">
                  {(["all", "electric", "servo-hydraulic"] as FilterType[]).map((f) => (
                    <button
                      key={f}
                      onClick={() => setFilter(f)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        filter === f
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {f === "all" ? t("equipment.allTypes") : f === "electric" ? t("equipment.electric") : t("equipment.servoHydraulic")}
                    </button>
                  ))}
                </div>
              )}

              {/* View toggle */}
              <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 rounded-md transition-colors ${viewMode === "grid" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                  title={t("equipment.viewGrid")}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 rounded-md transition-colors ${viewMode === "list" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                  title={t("equipment.viewList")}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Products */}
            {viewMode === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProducts.map((product, i) => (
                  <motion.div
                    key={product.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                  >
                    <ProductCardGrid product={product} lang={lang} t={t} showBadges={hasTypeFilter} />
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {filteredProducts.map((product, i) => (
                  <motion.div
                    key={product.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                  >
                    <ProductCardList product={product} lang={lang} t={t} showBadges={hasTypeFilter} />
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
